import { z } from "zod/v4";

import {
  AgenticLogicValidationScenarioDescriptions,
  AgenticLogicValidationScenarioInputDescriptions,
} from "../../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../../descriptions/types";
import {
  AgenticLogicContextSchema,
  AgenticLogicIngressRequestSchema,
} from "../types";

// ---------------------------------------------------------------------------
// Validation scenarios — the input substrate for tier 1 required tests and
// the chain_cost publish gate.
// ---------------------------------------------------------------------------

/**
 * One case the publish gates evaluate a rule against: the request-side inputs
 * only.
 *
 * There is deliberately no candidate-side half either. `invocation.candidates`
 * is RESERVED and nothing populates it in V1, so a scenario that supplied one
 * would pin an invocation shape serving can never produce — and every gate
 * downstream (required_tests_passed, stable_output, chain_cost,
 * runtime_timeout, and the AI suite generated from these scenarios) would then
 * measure the rule under conditions that do not exist. Reinstate it when the
 * field is activated; that is already gated on an envelope-version bump, so
 * the two land together.
 *
 * There is deliberately no read-side half. Gate-time SDK reads are served
 * LIVE, through the same broker that serves production traffic, so a scenario
 * pins what is asked of the rule and never what the org answers. That is the
 * whole reason recorded fixture bundles are gone: a pinned read log is a
 * second source of truth for the org's data that drifts from the real one the
 * moment the catalog changes, and it can only ever be as honest as whoever
 * recorded it.
 *
 * Determinism is therefore no longer a property of replay. It is measured:
 * every scenario is invoked TWICE against live reads and the envelopes must be
 * deeply equal (`stable_output`), and each test suite is run twice and its
 * per-test outcomes compared. A rule that reads `Math.random()`/`Date.now()`
 * fails that comparison; one that uses the runner-seeded `seededShuffle()`
 * passes.
 *
 * Time is the third case. A rule reads `request.evaluatedAt`, which the harness
 * stamps ONCE per scenario and reuses across both invocations — so branching on
 * it is deterministic by construction, not by luck. `evaluatedAt` here is the
 * author's override of that stamp, and pinning it is what makes a time-dependent
 * scenario stable: fix both ends and "a 30-day supply ordered 34 days ago"
 * evaluates the same way tomorrow.
 *
 * Pinning is also how a scenario states a BOUNDARY. When a branch is selected
 * by a comparison on the clock, the pinned instant is the edge value the
 * operator decides — and it is more deterministic than the stamp, not less:
 * the stamp is a different instant on every publish, the pin is the same
 * instant forever.
 */
export const AgenticLogicValidationScenarioSchema = z
  .object({
    name: z.string().describe(AgenticLogicValidationScenarioDescriptions.name),
    request: AgenticLogicIngressRequestSchema.describe(
      AgenticLogicValidationScenarioDescriptions.request
    ),
    context: AgenticLogicContextSchema.optional().describe(
      AgenticLogicValidationScenarioDescriptions.context
    ),
    evaluatedAt: z
      .number()
      .int()
      .positive()
      .optional()
      .describe(AgenticLogicValidationScenarioDescriptions.evaluatedAt),
    description: z
      .string()
      .max(500)
      .optional()
      .describe(AgenticLogicValidationScenarioDescriptions.description),
    targetBranchId: z
      .string()
      .nullable()
      .optional()
      .describe(AgenticLogicValidationScenarioDescriptions.targetBranchId),
    origin: z
      .enum(["authored", "generated"])
      .optional()
      .describe(AgenticLogicValidationScenarioDescriptions.origin),
  })
  .describe(AgenticLogicValidationScenarioDescriptions._schema);
export type AgenticLogicValidationScenario = z.infer<
  typeof AgenticLogicValidationScenarioSchema
>;

/**
 * The AUTHORING-side scenario, as the save-draft wire accepts it: identical to
 * the stored scenario except `request.placementId` may be omitted.
 *
 * A scenario's `placementId` is wire shape, not a reference — gates evaluate
 * the rule directly, never a placement assignment, and no placement need exist
 * when a rule is being authored. Requiring it on the wire made authoring tools
 * ask the user for a placement that means nothing here, so the server fills
 * the rule's own id at save time instead. The stored contract
 * ({@link AgenticLogicValidationScenarioSchema}) stays strict: everything
 * downstream of the draft save reads a fully-formed request.
 */
export const AgenticLogicValidationScenarioInputSchema =
  AgenticLogicValidationScenarioSchema.extend({
    request: AgenticLogicIngressRequestSchema.omit({ placementId: true })
      .extend({
        placementId: z
          .string()
          .optional()
          .describe(
            AgenticLogicValidationScenarioInputDescriptions.requestPlacementId
          ),
      })
      .describe(AgenticLogicValidationScenarioDescriptions.request),
  });
export type AgenticLogicValidationScenarioInput = z.infer<
  typeof AgenticLogicValidationScenarioInputSchema
>;

// The paged-scan strategy report this module's scan-cost gate prices lives in
// the SDK contract (../sdk/scan-strategy) — the SDK's paged-scan path emits
// it.

assertDescriptions<
  typeof AgenticLogicValidationScenarioDescriptions,
  AgenticLogicValidationScenario
>();
